import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type Task, task } from 'ember-concurrency';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { SubscriptionActiveFeatures } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface SubscriptionActiveFeaturesPayload {
  features: string[];
}

export default class SubscriptionActiveFeaturesService extends Service {
  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare store: Services['store'];

  @tracked loadedSubscriptionId: string | null = null;

  loadTask: Task<SubscriptionActiveFeatures, [string]> = task(
    { restartable: true },
    async (subscriptionId: string) => {
      try {
        const url = formatUrl(
          `/${APIGATEWAY}/channel-management/unified-settings/active-features`,
          { subscription_id: subscriptionId },
          this.ajax.host,
        );

        const response: SubscriptionActiveFeaturesPayload =
          await this.ajax.request(url, { method: 'GET' });

        const result = pushAndReturnPayload(
          this.store,
          {
            'subscription-active-features': [
              { ...response, id: subscriptionId },
            ],
          },
          'subscription/active-features',
        );

        this.loadedSubscriptionId = subscriptionId;

        return (
          result['subscription-active-features'] as SubscriptionActiveFeatures[]
        )[0]!;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get isLoaded(): boolean {
    return this.loadedSubscriptionId !== null;
  }

  hasFeature(featureName: string): boolean {
    if (!this.loadedSubscriptionId) {
      return false;
    }

    const record = this.store.peekRecord<SubscriptionActiveFeatures>(
      'subscription/active-features',
      this.loadedSubscriptionId,
    );

    return record?.features.includes(featureName) ?? false;
  }

  reset(): void {
    this.loadedSubscriptionId = null;

    if (this.store.isDestroyed || this.store.isDestroying) {
      return;
    }

    this.store.unloadAll('subscription/active-features');
  }
}

declare module '@ember/service' {
  interface Registry {
    'subscription-active-features': SubscriptionActiveFeaturesService;
  }
}
